"use client";

import { type KeyboardEvent, useMemo, useState } from "react";
import {
  CopyButton,
  Notice,
  PillGroup,
  SelectField,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  HINT,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import {
  COLUMNS,
  type Layout,
  type Paper,
  PrintSheets,
  ROWS,
  SHEET,
  type Sides,
} from "@/components/tools/study/flashcard-sheets";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { downloadText } from "@/lib/tool-kit/download";
import {
  type CardSeparator,
  cryptoRandomBelow,
  type DuplexFlip,
  type Flashcard,
  parseFlashcards,
  shuffled,
  toAnkiText,
  toQuizletText,
} from "@/lib/tool-kit/study/flashcards";

type Face = "term" | "definition";

const SEPARATORS: readonly CardSeparator[] = [
  "auto",
  "tab",
  "dash",
  "colon",
  "comma",
];

/** English strings. A translated content file overrides these through `ui`. */
export const FLASHCARD_MAKER_STRINGS = {
  inputLabel: "Your cards: one per line, term then definition",
  inputPlaceholder:
    "mitochondrion - where cellular respiration happens\nosmosis: movement of water across a membrane\nphotosynthesis, turning light into chemical energy",
  separatorLabel: "Separator between term and definition",
  separators: {
    auto: "Detect automatically",
    tab: "Tab",
    dash: "Dash ( - )",
    colon: "Colon ( : )",
    comma: "Comma ( , )",
  },
  cardCount: "one:{count} card|other:{count} cards",
  skippedLines:
    "one:Line {lines} has no separator, so it was skipped.|other:Lines {lines} have no separator, so they were skipped.",
  loadExample: "Try an example deck",
  exampleDeck:
    "mitochondrion - organelle where cellular respiration produces ATP\nribosome - site of protein synthesis\nosmosis - diffusion of water across a selectively permeable membrane\nenzyme - protein that speeds up a chemical reaction without being used up\nphotosynthesis - process that turns light energy, water and carbon dioxide into glucose and oxygen\nmitosis - cell division that produces two genetically identical cells",
  clear: "Clear",
  studyHeading: "Study",
  showFirst: "Show first",
  faces: { term: "Term", definition: "Definition" },
  studyHint:
    "Click the card or press Space to flip it. With the card selected, the left and right arrow keys move between cards and K marks the card as known.",
  flipCard: "Flip card",
  previous: "Previous",
  next: "Next",
  markKnown: "I know this",
  shuffle: "Shuffle",
  startOver: "Start over",
  progress: "Card {position} of {left} · {known} known",
  allKnown:
    "one:You know the card. Start over to go through it again.|other:You know all {count} cards. Start over to go through them again.",
  printHeading: "Print",
  paperLabel: "Paper",
  papers: { letter: "US Letter", a4: "A4" },
  layoutLabel: "Cards per page",
  layouts: { "6": "6 (2 × 3)", "8": "8 (2 × 4)", "10": "10 (2 × 5)" },
  flipLabel: "Double-sided printing",
  flips: { long: "Flip on long edge", short: "Flip on short edge" },
  sidesLabel: "Print",
  sides: { both: "Fronts and backs", fronts: "Fronts only" },
  sheetCount: "one:{count} page|other:{count} pages",
  printHint:
    "Fronts print on odd pages and backs on even pages, mirrored so each definition lands behind its term. Print double-sided at 100% scale with default margins, then cut along the dashed lines.",
  print: "Print cards ({pages})",
  exportHeading: "Export",
  copyQuizlet: "Copy for Quizlet",
  copied: "Copied!",
  downloadAnki: "Download for Anki (.txt)",
  ankiFileName: "flashcards-anki.txt",
  exportHint:
    "Quizlet: paste into a new set's Import box, with Tab between term and definition and New line between cards. Anki: choose File, then Import, and pick the downloaded file.",
  enterCards: "Type or paste at least one card to study, print or export.",
} as const;

interface StudyState {
  key: string;
  order: number[];
  position: number;
  known: number[];
  flipped: boolean;
}

function freshStudy(key: string, count: number): StudyState {
  return {
    key,
    order: Array.from({ length: count }, (_, index) => index),
    position: 0,
    known: [],
    flipped: false,
  };
}

export default function FlashcardMakerWidget() {
  const strings = useToolUi(FLASHCARD_MAKER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [text, setText] = useState("");
  const [separator, setSeparator] = useState<CardSeparator>("auto");
  const [firstFace, setFirstFace] = useState<Face>("term");
  const [study, setStudy] = useState<StudyState>(freshStudy("", 0));
  const [paper, setPaper] = useState<Paper>("letter");
  const [layout, setLayout] = useState<Layout>("8");
  const [flip, setFlip] = useState<DuplexFlip>("long");
  const [sides, setSides] = useState<Sides>("both");

  const deck = useMemo(
    () => parseFlashcards(text, separator),
    [text, separator]
  );
  const cards = deck.cards;
  const deckKey = useMemo(() => JSON.stringify(cards), [cards]);
  const active =
    study.key === deckKey ? study : freshStudy(deckKey, cards.length);
  const remaining = active.order.filter(
    (index) => !active.known.includes(index)
  );
  const position = Math.min(active.position, Math.max(0, remaining.length - 1));
  const current = remaining.length > 0 ? cards[remaining[position]] : undefined;
  const perPage = COLUMNS * ROWS[layout];
  const pageCount =
    Math.ceil(cards.length / perPage) * (sides === "both" ? 2 : 1);

  const go = (patch: Partial<StudyState>) => setStudy({ ...active, ...patch });
  const move = (step: number) => {
    if (remaining.length === 0) {
      return;
    }
    go({
      position: (position + step + remaining.length) % remaining.length,
      flipped: false,
    });
  };
  const markKnown = () => {
    if (remaining.length > 0) {
      go({ known: [...active.known, remaining[position]], flipped: false });
    }
  };
  const shuffle = () =>
    go({
      order: shuffled(active.order, cryptoRandomBelow),
      position: 0,
      flipped: false,
    });

  function handleKeys(event: KeyboardEvent<HTMLButtonElement>) {
    if (event.key === "ArrowRight") {
      event.preventDefault();
      move(1);
    } else if (event.key === "ArrowLeft") {
      event.preventDefault();
      move(-1);
    } else if (event.key === "k" || event.key === "K") {
      markKnown();
    }
  }

  const faceText = (card: Flashcard, face: Face) =>
    face === "term" ? card.term : card.definition;
  const secondFace: Face = firstFace === "term" ? "definition" : "term";

  return (
    <>
      <style>{`@media print { @page { size: ${SHEET[paper].page} portrait; margin: ${SHEET[paper].margin}; } }`}</style>
      <section className="space-y-3 print:hidden">
        <TextAreaField
          id="fc-input"
          label={strings.inputLabel}
          onChange={setText}
          placeholder={strings.inputPlaceholder}
          prose
          rows={8}
          value={text}
        />
        <div className="grid gap-4 sm:grid-cols-2 sm:items-end">
          <SelectField
            id="fc-separator"
            label={strings.separatorLabel}
            onChange={setSeparator}
            options={SEPARATORS.map((value) => ({
              value,
              label: strings.separators[value],
            }))}
            value={separator}
          />
          <div className="flex flex-wrap gap-3">
            <button
              className={BUTTON_SECONDARY}
              onClick={() => setText(strings.exampleDeck)}
              type="button"
            >
              {strings.loadExample}
            </button>
            <button
              className={BUTTON_SECONDARY}
              disabled={text === ""}
              onClick={() => setText("")}
              type="button"
            >
              {strings.clear}
            </button>
          </div>
        </div>
        <p aria-live="polite" className={HINT}>
          {tPlural(strings.cardCount, locale, cards.length, {
            count: fmt(cards.length),
          })}
          {deck.skipped.length > 0
            ? ` · ${tPlural(strings.skippedLines, locale, deck.skipped.length, {
                lines: deck.skipped.join(", "),
              })}`
            : null}
        </p>
      </section>

      {cards.length === 0 ? (
        <div className="print:hidden">
          <Notice>{strings.enterCards}</Notice>
        </div>
      ) : (
        <>
          <section className="space-y-4 print:hidden">
            <h2 className={WIDGET_H2}>{strings.studyHeading}</h2>
            <PillGroup
              label={strings.showFirst}
              name="fc-first"
              onChange={setFirstFace}
              options={[
                { value: "term", label: strings.faces.term },
                { value: "definition", label: strings.faces.definition },
              ]}
              value={firstFace}
            />
            <div className="space-y-4">
              {current ? (
                <button
                  aria-label={strings.flipCard}
                  className="group block h-64 w-full [perspective:1200px] focus:outline-none"
                  onClick={() => go({ flipped: !active.flipped })}
                  onKeyDown={handleKeys}
                  type="button"
                >
                  <span
                    className={`relative block h-full w-full transition-transform duration-500 [transform-style:preserve-3d] motion-reduce:transition-none ${active.flipped ? "[transform:rotateY(180deg)]" : ""}`}
                  >
                    {([firstFace, secondFace] as const).map((face, side) => (
                      <span
                        aria-hidden={(side === 1) !== active.flipped}
                        className={`absolute inset-0 flex flex-col items-center justify-center gap-3 overflow-auto rounded-xl border-2 border-border bg-card p-6 text-center [backface-visibility:hidden] group-focus-visible:ring-2 group-focus-visible:ring-primary ${side === 1 ? "[transform:rotateY(180deg)]" : ""}`}
                        key={face}
                      >
                        <span className="text-muted-foreground text-xs uppercase tracking-wide">
                          {strings.faces[face]}
                        </span>
                        <span
                          className={`text-foreground leading-snug ${face === "term" ? "font-semibold text-2xl" : "text-lg"}`}
                        >
                          {faceText(current, face)}
                        </span>
                      </span>
                    ))}
                  </span>
                </button>
              ) : (
                <Notice>
                  {tPlural(strings.allKnown, locale, cards.length, {
                    count: fmt(cards.length),
                  })}
                </Notice>
              )}
              <p
                aria-live="polite"
                className="text-center text-muted-foreground text-sm"
              >
                {t(strings.progress, {
                  position: fmt(remaining.length > 0 ? position + 1 : 0),
                  left: fmt(remaining.length),
                  known: fmt(active.known.length),
                })}
              </p>
              <div className="flex flex-wrap justify-center gap-3">
                <button
                  className={BUTTON_SECONDARY}
                  disabled={remaining.length < 2}
                  onClick={() => move(-1)}
                  type="button"
                >
                  {strings.previous}
                </button>
                <button
                  className={BUTTON_PRIMARY}
                  disabled={!current}
                  onClick={markKnown}
                  type="button"
                >
                  {strings.markKnown}
                </button>
                <button
                  className={BUTTON_SECONDARY}
                  disabled={remaining.length < 2}
                  onClick={() => move(1)}
                  type="button"
                >
                  {strings.next}
                </button>
                <button
                  className={BUTTON_SECONDARY}
                  disabled={remaining.length < 2}
                  onClick={shuffle}
                  type="button"
                >
                  {strings.shuffle}
                </button>
                <button
                  className={BUTTON_SECONDARY}
                  onClick={() => setStudy(freshStudy(deckKey, cards.length))}
                  type="button"
                >
                  {strings.startOver}
                </button>
              </div>
              <p className={`${HINT} text-center`}>{strings.studyHint}</p>
            </div>
          </section>

          <section className="space-y-4 print:hidden">
            <h2 className={WIDGET_H2}>{strings.printHeading}</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <SelectField
                id="fc-paper"
                label={strings.paperLabel}
                onChange={setPaper}
                options={[
                  { value: "letter", label: strings.papers.letter },
                  { value: "a4", label: strings.papers.a4 },
                ]}
                value={paper}
              />
              <SelectField
                id="fc-layout"
                label={strings.layoutLabel}
                onChange={setLayout}
                options={(["6", "8", "10"] as const).map((value) => ({
                  value,
                  label: strings.layouts[value],
                }))}
                value={layout}
              />
              <SelectField
                id="fc-sides"
                label={strings.sidesLabel}
                onChange={setSides}
                options={[
                  { value: "both", label: strings.sides.both },
                  { value: "fronts", label: strings.sides.fronts },
                ]}
                value={sides}
              />
              <SelectField
                id="fc-flip"
                label={strings.flipLabel}
                onChange={setFlip}
                options={[
                  { value: "long", label: strings.flips.long },
                  { value: "short", label: strings.flips.short },
                ]}
                value={flip}
              />
            </div>
            <p className={HINT}>{strings.printHint}</p>
            <button
              className={BUTTON_PRIMARY}
              onClick={() => window.print()}
              type="button"
            >
              {t(strings.print, {
                pages: tPlural(strings.sheetCount, locale, pageCount, {
                  count: fmt(pageCount),
                }),
              })}
            </button>
          </section>

          <section className="space-y-3 print:hidden">
            <h2 className={WIDGET_H2}>{strings.exportHeading}</h2>
            <div className="flex flex-wrap gap-3">
              <CopyButton
                copiedLabel={strings.copied}
                label={strings.copyQuizlet}
                text={toQuizletText(cards)}
              />
              <button
                className={BUTTON_SECONDARY}
                onClick={() =>
                  downloadText(strings.ankiFileName, toAnkiText(cards))
                }
                type="button"
              >
                {strings.downloadAnki}
              </button>
            </div>
            <p className={HINT}>{strings.exportHint}</p>
          </section>

          <PrintSheets
            cards={cards}
            flip={flip}
            layout={layout}
            paper={paper}
            sides={sides}
          />
        </>
      )}
    </>
  );
}
